<template>
	<!-- The gate keeps the logged-out shell out of prerendered HTML: static
	builds bake the loading state and only reveal the shell after the client
	auth check has resolved (see AuthBootGate). -->
	<AuthBootGate>
		<UiNavigationSidebarLayout
			class="layout-default"
			expanded-width="200px"
			collapsed-width="calc(44px + 2 * 0.8rem)"
			mobile-breakpoint="900px"
		>
			<template #sidebar>
				<UiNavigationSidebar
					ref="sidebarElement"
					v-model:locked="isUserSidebarLocked"
					:model-value="isSidebarOpen"
					:no-home="hasCustomHomePage"
					prefer-width
					class="sidebar"
					@update:model-value="toggleOpen"
					@selecting-account="toggleLock"
				>
					<template #header>
						<img
							:src="displayLogo || '/logo.png'"
							alt="Logo"
							class="logo"
							:class="`logo--${logoFit}`"
						/>
					</template>

					<UiNavigationSidebarLinkList
						v-model:mode="sidebarMode"
						:items="sidebarItems"
						:expanded="isSidebarExpanded"
						@reorder="handleReorder"
						@hide="handleHide"
					/>

					<template #footer>
						<AuthAccountSelector
							:open="isSidebarExpanded"
							:profile-name="
								auth.selectedAccount.value?.first_name ||
								auth.selectedAccount.value?.company
							"
							:options="auth.childAccountsOptions.value"
							:model-value="auth.getSelectedAccountOption.value"
							:loading="auth.isLoadingAccounts.value || auth.isChangingUser.value"
							:error="auth.accountsError.value"
							@update:model-value="
								(val: string) => (auth.getSelectedAccountOption.value = val)
							"
							@selecting-account="handleSelectingAccount"
						/>
					</template>
				</UiNavigationSidebar>
			</template>

			<slot />

			<template #mobile-navbar>
				<UiNavigationNavbarMobileLinkList
					:items="sidebarItems"
					:no-home="hasCustomHomePage"
				/>
			</template>
		</UiNavigationSidebarLayout>
	</AuthBootGate>
</template>

<script lang="ts" setup>
import { UiNavigationSidebar } from '#components';
import { useUserPreferences } from '~~/app/composables/useUserPreferences';

const auth = useAuth();
const { enabledPages, branding, homePage, productType } = useCustomerConfig();
const hasCustomHomePage = computed(() => !!homePage.value);
const { logo, logoSquare } = useCustomerLogos();
const { getSidebarPreferences, setSidebarItemOrder, setSidebarItemVisibility } =
	useUserPreferences();

// Use useSidebar directly - useCookie shares state by key across components
const { isSidebarOpen, isUserSidebarLocked, toggleOpen, toggleLock } = useSidebar();
const isAccountSelectorOpen = ref(false);

const sidebarMode = ref<'default' | 'edit' | 'reorder'>('default');

const isSidebarExpanded = computed(
	() => isSidebarOpen.value && isUserSidebarLocked.value !== 'pinned-shut',
);

const logoFit = computed(() => {
	if (!isSidebarExpanded.value && branding.value?.assets?.logoSquare) {
		return branding.value?.assets?.logoSquareFit ?? 'height';
	}
	return branding.value?.assets?.logoFit ?? 'height';
});
const displayLogo = computed(() =>
	!isSidebarExpanded.value && branding.value?.assets?.logoSquare
		? logoSquare.value
		: logo.value || '/logo.png',
);

const sidebarItems = computed(() => {
	const prefs = getSidebarPreferences();
	return enabledPages.value
		.filter((page) => prefs.items[page.id]?.visible !== false)
		.sort((a, b) => {
			const orderA = prefs.items[a.id]?.order ?? a.order ?? 0;
			const orderB = prefs.items[b.id]?.order ?? b.order ?? 0;
			return orderA - orderB;
		})
		.map((page) => ({
			id: page.id,
			to: page.path,
			icon: page.icon === 'truck' && productType.value === 'helicopter' ? 'helicopter' : page.icon,
			title: page.title,
		}));
});

function handleReorder(fromIndex: number, toIndex: number) {
	const items = sidebarItems.value;
	const movedItem = items[fromIndex];
	if (!movedItem) return;

	// Create new order for all items based on their new positions
	const newItems = [...items];
	newItems.splice(fromIndex, 1);
	newItems.splice(toIndex, 0, movedItem);

	// Update order preferences for each item
	newItems.forEach((item, index) => {
		setSidebarItemOrder(item.id, index);
	});
}

function handleHide(itemId: string) {
	setSidebarItemVisibility(itemId, false);
}

function handleSelectingAccount(selecting: boolean) {
	isAccountSelectorOpen.value = selecting;
}
</script>

<style scoped lang="scss">
.sidebar {
	display: flex;
}

.logo--height {
	height: 100% !important;
	width: auto !important;
}

.logo--width {
	width: 100% !important;
	height: auto !important;
}

.account-changer {
	padding: 0.75rem;
	border-top: 1px solid var(--background-100);

	:deep() .account-selector {
		max-width: 122px;
	}
	:deep() .account-selector-wrapper {
		min-width: 122px;
	}
}
</style>
